/**
 * useEngagement Hook
 *
 * Real-time listener for a single engagement session.
 * Used by the Session Detail page.
 */

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Engagement } from '../models/Engagement';

/**
 * @param {string|null} sessionId - The engagement session ID (Firestore doc key)
 * @returns {{ session: Engagement|null, loading: boolean, error: Error|null }}
 */
export const useEngagement = (sessionId = null) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setSession(null);
      return;
    }

    setLoading(true);
    setError(null);

    // 15s timeout guard — prevents infinite spinner if Firestore channel is stale
    const timeoutId = setTimeout(() => {
      setLoading(current => {
        if (current) {
          setError(new Error('Firestore connection timed out. Hard-refresh (Cmd+Shift+R) to fix.'));
          return false;
        }
        return current;
      });
    }, 15000);

    const unsubscribe = onSnapshot(
      doc(db, 'engagement', sessionId),
      (docSnap) => {
        clearTimeout(timeoutId);
        if (docSnap.exists()) {
          setSession(Engagement.fromFirestore(docSnap));
          setError(null);
        } else {
          setSession(null);
          setError(new Error('Session not found'));
        }
        setLoading(false);
      },
      (err) => {
        clearTimeout(timeoutId);
        console.error('[useEngagement] Listener error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [sessionId]);

  return { session, loading, error };
};
